// StacksView: Zeigt Karten in Kategorien/Stapeln an mit Drag & Drop
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UiCardImage from '../../../ui/CardImage';
import CardOverlays from '../../../ui/CardOverlays';
import { useCardPeek } from '../../../ui/useCardPeek';
import { Minus, Plus } from 'lucide-react';
import CategoryHeader from '../../../ui/CategoryHeader';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import { fittingColumnCount } from '../utils/columnFit';
import { formatPrice } from '../utils/formatPrice';
import { useDeckCardActions } from '../hooks/useDeckCardActions';
import { useDeckViewState } from '../hooks/useDeckViewState';
import { useShouldBeTransparent } from '../hooks/useShouldBeTransparent';
import { useDeckDragHandlers } from '../hooks/useDeckDragHandlers';
import CardContextMenu from '../components/CardContextMenu.js';
import LabelIndicators from '../components/LabelIndicators.js';
import '../../../components/CardViews.css';

// ========== KONFIGURATIONSKONSTANTEN ==========

// MTG-Karten Seitenverhältnis — lokal statt Import aus shared/CardBase: CardInStack
// rendert nicht über CardBase, braucht die Konstante aber für die
// Masonry-/Overlap-Höhenberechnung des Wrappers.
const CARD_ASPECT_RATIO = 680 / 488;

const CARD_MIN_WIDTH = 50;
const CARD_MAX_WIDTH = 400;
const CARD_GAP = 9;                   // Abstand zwischen Stapeln
const CARD_OVERLAP_DIVISOR = 8.1;     // Je höher, desto weniger pro Karte sichtbar
const HOVER_EXPAND_PERCENT = 88;      // Wieviel % der Kartenhöhe beim Hover sichtbar wird
const HOVER_EXPAND_GAP_PX = 8;        // Zusätzlicher Spalt unter der gehoverten Karte (ersetzt den früheren -3px-Lift, der die Trennung erzeugte)
const HOVER_ENTER_DELAY_MS = 100;     // Verzögerung bis Hover aktiviert
const HOVER_LEAVE_DELAY_MS = 150;     // Verzögerung bis Hover deaktiviert
const HOVER_TRANSITION_MS = 350;      // Animationsdauer für Karten-Slide
const HOVER_STAGGER_MS = 37;          // Verzögerung zwischen Karten in der Kaskade
const STACK_PADDING = 2;
const STACK_BORDER_PX = 2;
const STACK_HEADER_HEIGHT = 60;
const HEADER_TO_CARDS_GAP = 8;        // Minimaler Abstand Header -> erste Karte
const ROW_SNAP_THRESHOLD_PX = 225;

// ========== KARTEN-KOMPONENTEN ==========

// Einzelne Karte im Stapel — empfängt Hover-Zustand vom Parent (CategoryStack)
function CardInStack({
  card, isFirst, cardWidth, visibleHeight,
  disableHover = false,
  canEditQuantity = false, onQuantityDelta, isOwnProfile = false,
  onSetAsCommander, onShowDetails, onSwitchPrint,
  index, onHoverEnter, onHoverLeave,
  expandTranslateY, transitionDelay,
  highlightedNewCardId,
  deckLabels, cardLabels, onAssignLabel, onRemoveLabel, onNewLabelRequest,
}) {
  const cardHeight = cardWidth * CARD_ASPECT_RATIO;
  const hiddenPortion = cardHeight - visibleHeight;
  const overlapOffset = isFirst ? 0 : -hiddenPortion;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card-${card.id}`,
    data: { card },
  });
  // Querformat-Karten: der Wrapper ist zugleich das Bild (kein Info-Feld darunter).
  const peekRef = useRef(null);
  const { peek, handlers: peekHandlers } = useCardPeek({ imageRef: peekRef });
  const setRefs = useCallback((el) => { setNodeRef(el); peekRef.current = el; }, [setNodeRef]);

  const handleSetCommander = useCallback(() => {
    if (onSetAsCommander) {
      onSetAsCommander(card.id, true);
    }
  }, [card.id, onSetAsCommander]);

  // Zwei unabhängige Sichtbarkeits-Bedingungen (heutiges Verhalten 1:1 erhalten):
  // Mengen-Buttons brauchen Edit-Recht, das ⋮-Menü ist auch bei reinem Lese-Zugriff
  // (isOwnProfile bei physischem Deck, canEditQuantity=false) verfügbar — z.B. um
  // "Als Commander setzen" auszulösen. Beides zusammen zu verschmelzen würde diesen
  // Fall regressen.
  const canEditQty = isOwnProfile && canEditQuantity && typeof onQuantityDelta === 'function' && !isDragging;
  // „Details" steht auch ohne Bearbeitungsrecht offen (fremdes Deck) — deshalb öffnet das
  // Menü dort ebenfalls; alle mutierenden Einträge bleiben unten an isOwnProfile gebunden.
  const canShowMenu = (isOwnProfile || (typeof onShowDetails === 'function' && !!card.oracleId)) && !isDragging;

  const handleQuantityDelta = useCallback((delta) => {
    const id = card.scryfallId || card.id || card['Scryfall ID'];
    if (id) onQuantityDelta(id, delta);
  }, [card, onQuantityDelta]);

  return (
    <div
      ref={setRefs}
      {...listeners}
      {...attributes}
      {...peekHandlers}
      data-card-id={card.id || card.scryfallId}
      className={`card-in-stack ${isDragging ? 'is-dragging' : ''}`}
      style={{
        width: cardWidth,
        height: cardHeight,
        marginTop: overlapOffset,
        transform: expandTranslateY ? `translateY(${expandTranslateY}px)` : undefined,
        // Kein Hover-Schatten/-Lift auf der Karte (Nutzer-Entscheid) — nur der
        // statische Playmat-Physik-Filter (.de-playmat .card-in-stack). isHovered
        // steuert weiterhin nur die Expand-Kaskade (Höhe + Wegschieben darunter).
        transition: `transform ${HOVER_TRANSITION_MS}ms ease-out ${transitionDelay}ms, filter ${HOVER_TRANSITION_MS}ms ease-out`,
        opacity: isDragging ? 0 : 1,
      }}
      // Hover-Erkennung für die Expand-Kaskade sitzt am Karten-Wrapper: ein
      // separates Hotspot-Element darunter würde vom Kartenbild (.card-image,
      // z-index 3 für klickbare Overlay-Buttons) verdeckt und bekäme nie ein
      // onMouseEnter — das Auffächern bliebe tot. Der Wrapper umschließt das
      // Bild — mouseenter trifft ihn zuverlässig, z-index-unabhängig, und die
      // Overlay-Buttons bleiben klickbar.
      onMouseEnter={() => { if (!disableHover) onHoverEnter(index); }}
      onMouseLeave={() => { if (!disableHover) onHoverLeave(); }}
    >
      <LabelIndicators labels={cardLabels} variant="tabs" />
      <UiCardImage
        src={card.imageUrl}
        backSrc={card.imageUrlBack}
        alt={card.name}
        landscape="peek"
        peek={peek}
        orientation={card.imageOrientation}
        onOpen={onShowDetails && card.oracleId ? () => onShowDetails(card) : undefined}
        openLabel={`${card.name} — Details`}
      >
        <CardOverlays>
          <CardOverlays.Corner position="top-left">
            {(card.quantity || 0) > 1 && (
              <CardOverlays.Badge>×{card.quantity}</CardOverlays.Badge>
            )}
            {Number(card.price) > 0 && (
              <CardOverlays.Badge hover>{formatPrice(card.price)}</CardOverlays.Badge>
            )}
          </CardOverlays.Corner>
          {(canEditQty || canShowMenu) && (
            <CardOverlays.Actions>
              {canEditQty && (
                <>
                  <CardOverlays.Button icon={Minus} label="Eine Kopie entfernen" onClick={() => handleQuantityDelta(-1)} />
                  <CardOverlays.Button icon={Plus} label="Eine Kopie hinzufügen" onClick={() => handleQuantityDelta(1)} />
                </>
              )}
              {canShowMenu && (
                <CardContextMenu
                  overlayTrigger
                  card={card}
                  onShowDetails={onShowDetails && card.oracleId ? onShowDetails : undefined}
                  onSwitchPrint={onSwitchPrint && card.oracleId ? onSwitchPrint : undefined}
                  onSetCommander={isOwnProfile ? handleSetCommander : undefined}
                  deckLabels={deckLabels}
                  cardLabels={cardLabels}
                  onAssignLabel={(label) => onAssignLabel(card.deckCardEntryId, label)}
                  onRemoveLabel={(labelId) => onRemoveLabel(card.deckCardEntryId, labelId)}
                  onNewLabel={isOwnProfile ? () => onNewLabelRequest(card.deckCardEntryId) : undefined}
                />
              )}
            </CardOverlays.Actions>
          )}
          <CardOverlays.HighlightRing active={(card.id || card.scryfallId) === highlightedNewCardId} />
        </CardOverlays>
      </UiCardImage>
    </div>
  );
}

// ========== KATEGORIE-KOMPONENTEN ==========

// Ein Stapel von Karten (Kategorie) — verwaltet Hover-Zustand zentral
function CategoryStack({ category, categoryId, excluded, cards, isDropTarget, cardWidth, visibleHeight, disableHover, canEditQuantity, onQuantityDelta, isOwnProfile, onSetAsCommander, onShowDetails, onSwitchPrint, onRemoveCategory, canRemove, isDraggingCategory, isDroppable, externalDragging = false, externalDropProps, style: externalStyle, stickyTop = 0, highlightedNewCardId, deckLabels, getCardLabels, onAssignLabel, onRemoveLabel, onNewLabelRequest, strategy, renameCategory, onCategoryRenamed }) {
  // Cards sind vom Parent bereits gefiltert
  const visibleCards = cards;

  const totalCards = useMemo(() => 
    visibleCards.reduce((sum, c) => sum + (c.quantity || 1), 0),
    [visibleCards]
  );
  const totalPrice = useMemo(
    () => visibleCards.reduce((sum, c) => sum + ((Number(c.price) || 0) * (Number(c.quantity) || 0)), 0),
    [visibleCards]
  );

  // --- Zentrales Hover-Management ---
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const prevHoveredRef = useRef(null);
  const hoverTimerRef = useRef(null);

  // Merke den letzten aktiven Hover-Index für die Collapse-Kaskade
  useEffect(() => {
    if (hoveredIndex !== null) {
      prevHoveredRef.current = hoveredIndex;
    }
  }, [hoveredIndex]);

  const handleCardHoverEnter = useCallback((idx) => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredIndex(idx), HOVER_ENTER_DELAY_MS);
  }, []);

  const handleCardHoverLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredIndex(null), HOVER_LEAVE_DELAY_MS);
  }, []);

  useEffect(() => { if (disableHover) { clearTimeout(hoverTimerRef.current); setHoveredIndex(null); } }, [disableHover]);
  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  const expandPx = Math.round(cardWidth * CARD_ASPECT_RATIO * HOVER_EXPAND_PERCENT / 100) + HOVER_EXPAND_GAP_PX;

  // Berechne für jede Karte: translateY und transition-delay
  // Expand (hoveredIndex gesetzt): Alle Karten darunter sofort zusammen runter (delay=0)
  // Collapse (hoveredIndex null): Karten nah am vorherigen Hover zuerst, weiter entfernte mit Stagger
  const getCardAnimation = useCallback((idx) => {
    if (hoveredIndex !== null) {
      // Expanding: Karten unterhalb sofort runterschieben
      if (idx > hoveredIndex) {
        return { translateY: expandPx, delay: 0 };
      }
      return { translateY: 0, delay: 0 };
    }
    // Collapsing: Kaskade vom vorherigen Hover-Punkt nach außen
    const prev = prevHoveredRef.current;
    if (prev !== null && idx > prev) {
      const distance = idx - prev - 1;
      return { translateY: 0, delay: distance * HOVER_STAGGER_MS };
    }
    return { translateY: 0, delay: 0 };
  }, [hoveredIndex, expandPx]);

  const cardHeightFull = cardWidth * (680 / 488);
  const minHeight = cardHeightFull + 30;
  
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `category-${category}`,
    data: { category, categoryId },
    disabled: !isDroppable,
  });
  const { setNodeRef: setSortRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: `cat-${category}` });
  const setCombinedRef = (el) => {
    setDropRef(el);
    setSortRef(el);
  };
  const styleTransform = transform
    ? CSS.Transform.toString({
      ...transform,
      scaleX: 1,
      scaleY: 1,
    })
    : undefined;
  const showAsDropTarget = !isDraggingCategory && (isDropTarget || isOver);
  
  const handleRemoveClick = useCallback(() => {
    if (onRemoveCategory) {
      onRemoveCategory(category);
    }
  }, [category, onRemoveCategory]);

  // Rename nur für echte, persistierte Kategorien des eigenen Decks
  // (nicht 'other'/Nicht-Kategorie-Gruppierungen, nicht Commander — der hat keinen Header hier).
  const canRenameCategory = Boolean(isOwnProfile && strategy?.persistent && categoryId != null);

  const handleRenameCategory = useCallback(async (nextName) => {
    const result = await renameCategory?.(categoryId, nextName);
    onCategoryRenamed?.(category, result);
  }, [categoryId, category, renameCategory, onCategoryRenamed]);

  return (
    <div
      ref={setCombinedRef}
      {...attributes}
      data-drop-group={category}
      data-external-target={isDropTarget && externalDragging ? 'true' : undefined}
      {...externalDropProps}
      className="card-overlays-scope"
      style={{
        boxSizing: 'border-box',
        width: cardWidth + STACK_PADDING * 2 + STACK_BORDER_PX * 2,
        minHeight: minHeight,
        padding: `${STACK_PADDING}px`,
        borderRadius: '4px',
        // Die Ablagefläche selbst bleibt immer transparent — nur der Drop-Ziel-
        // Zustand bekommt eine sichtbare Färbung (Tint + gestrichelter Rahmen).
        // Ein opakes --bg-surface im Ruhezustand würde beim Wechsel auf das
        // durchscheinende --accent-tint inkonsistent wirken, als verschwände
        // der Hintergrund beim Drag.
        backgroundColor: showAsDropTarget ? 'var(--accent-tint)' : 'transparent',
        // Der Ruhezustand-Rahmen ist ebenfalls transparent — passend zum
        // transparenten Hintergrund. Bleibt aber `2px solid transparent` statt
        // `none`, damit die Box (boxSizing: border-box) exakt so breit bleibt
        // wie im Drop-Ziel-Zustand (weiterhin 2px Rahmenbreite) und beim Drag
        // kein Layout-Sprung entsteht.
        border: showAsDropTarget
          ? '2px dashed var(--accent)'
          : externalDragging
            ? '2px dashed var(--accent-hint)'
            : '2px solid transparent',
        transition,
        cursor: showAsDropTarget ? 'copy' : 'default',
        transform: styleTransform,
        opacity: isDragging ? 0.4 : 1,
        ...externalStyle,
      }}
    >
      {/* Header — dragbar für Kategorie-Sortierung */}
      <CategoryHeader
        name={category}
        count={totalCards}
        price={formatPrice(totalPrice)}
        excluded={excluded}
        dragHandle={isOwnProfile}
        dragListeners={isOwnProfile ? listeners : undefined}
        isDropTarget={showAsDropTarget}
        stickyTop={stickyTop}
        canRename={canRenameCategory}
        onRename={(next) => handleRenameCategory(next)}
        canRemove={canRemove && isOwnProfile}
        onRemove={handleRemoveClick}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          // CategoryHeader trägt kein margin-bottom (Table/Rows/Compact brauchen
          // dort keins) — den Abstand zur ersten Karte trägt deshalb der
          // Stack-Container.
          marginTop: `${HEADER_TO_CARDS_GAP}px`,
        }}
      >
        {visibleCards.map((card, idx) => {
          const { translateY, delay } = getCardAnimation(idx);

          return (
            <CardInStack
              key={`${card.id}-${idx}`}
              card={card}
              isFirst={idx === 0}
              cardWidth={cardWidth}
              visibleHeight={visibleHeight}
              disableHover={disableHover}
              canEditQuantity={canEditQuantity}
              onQuantityDelta={onQuantityDelta}
              isOwnProfile={isOwnProfile}
              onSetAsCommander={onSetAsCommander}
              onShowDetails={onShowDetails}
              onSwitchPrint={onSwitchPrint}
              index={idx}
              onHoverEnter={handleCardHoverEnter}
              onHoverLeave={handleCardHoverLeave}
              expandTranslateY={translateY}
              transitionDelay={delay}
              highlightedNewCardId={highlightedNewCardId}
              deckLabels={deckLabels}
              cardLabels={getCardLabels(card.deckCardEntryId)}
              onAssignLabel={onAssignLabel}
              onRemoveLabel={onRemoveLabel}
              onNewLabelRequest={onNewLabelRequest}
            />
          );
        })}
        {/* Drop-Animations-Placeholder: unsichtbares Mess-Ziel für die
            Lande-Position der nächsten Karte. marginTop: -hiddenPortion zieht
            ihn analog zu CardInStack über die letzte Karte → placeholder.top
            entspricht exakt der nächsten Slot-Top-Position. Bei 0 Karten
            ohne Margin am Container-Top.
            height: 4 hält den Placeholder innerhalb des Stack-Footprints,
            damit das Masonry-Layout unverändert bleibt. */}
        <div
          className="card-drop-placeholder card-drop-placeholder--stacks"
          data-placeholder-category={category}
          style={{
            width: cardWidth,
            height: 4,
            marginTop: visibleCards.length === 0 ? 0 : -(cardHeightFull - visibleHeight),
          }}
        />
      </div>
    </div>
  );
}

// ========== HAUPTKOMPONENTE ==========

export default function StacksView({ 
  externalDrop,
  deck, 
  isOwnProfile, 
  onCategoryChange, 
  canEditQuantity = false,
  onQuantityDelta,
  columnCount = 6,
  sortBy = 'name',
  searchTerm = '',
  highlightedCmc = null,
  strategy,
  matchingCardIds = null,
  isDraggingCategory = false,
  onRegisterDndHandlers,
  showExcluded = true,
  onRemoveCardsByCategory,
  onShowDetails,
  onSwitchPrint,
  stickyTop = 0,
  highlightedNewCardId,
  deckLabels,
  getCardLabels,
  onAssignLabel,
  onRemoveLabel,
  onNewLabelRequest,
  onDropError,
  onCategoryRenamed,
  onCardMoved,
  onCatalogCardDrop,
}) {
  const { authToken } = useAuth();

  // Hook für Deck-Karten-Aktionen (setAsCommander)
  const { setAsCommander } = useDeckCardActions({
    deck,
    canEdit: isOwnProfile,
    onError: onDropError,
  });

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Berechne Container-Breite für Spaltenberechnung
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateWidth = () => {
      if (!containerRef.current) return;
      setContainerWidth(containerRef.current.clientWidth);
    };
    
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);
    
    return () => observer.disconnect();
  }, []);
  
  // Tatsächlich gerenderte Spaltenzahl (Kappung siehe utils/columnFit.js): jeder Stapel
  // trägt zur Karte noch Innenabstand und Rahmen bei.
  const effectiveColumnCount = useMemo(() => fittingColumnCount({
    containerWidth,
    columnCount,
    minCardWidth: CARD_MIN_WIDTH,
    gap: CARD_GAP,
    perColumnExtra: STACK_PADDING * 2 + STACK_BORDER_PX * 2,
  }), [containerWidth, columnCount]);

  // Berechne Kartenbreite basierend auf Spaltenanzahl und Container-Breite
  const cardWidth = useMemo(() => {
    if (containerWidth === 0) return CARD_MIN_WIDTH;
    const totalGaps = (effectiveColumnCount - 1) * CARD_GAP;
    const totalStackExtras = effectiveColumnCount * (STACK_PADDING * 2 + STACK_BORDER_PX * 2);
    const availableWidth = containerWidth - totalGaps - totalStackExtras;
    const calculated = Math.floor(availableWidth / effectiveColumnCount);
    return Math.max(CARD_MIN_WIDTH, Math.min(CARD_MAX_WIDTH, calculated));
  }, [containerWidth, effectiveColumnCount]);
  
  // Berechne die sichtbare Höhe pro Karte (für Stacks-Ansicht)
  const cardHeight = cardWidth * (680 / 488);
  const visibleHeight = Math.round(cardHeight / CARD_OVERLAP_DIVISOR);
  
  // Gemeinsame Hooks für View-State, Transparenz und DnD
  const { groups, categories, moveCategory, removeCategory, addCategory, renameCategory, excludedCategoryNames } = useDeckViewState({
    deck, sortBy, strategy, showExcluded, isOwnProfile, authToken,
  });

  const shouldBeTransparent = useShouldBeTransparent({ highlightedCmc, searchTerm, matchingCardIds });
  const isFilterActive = !!(searchTerm?.trim() || highlightedCmc !== null || matchingCardIds !== null);

  const { dropTarget, handleRemoveCategory, handleSetAsCommander, hoverSuppressed, pendingDeleteCategory, confirmDeleteCategory, cancelDeleteCategory, applyManualOrder } = useDeckDragHandlers({
    deck, isOwnProfile, onCategoryChange, categories,
    moveCategory, removeCategory, addCategory, sortBy, setAsCommander,
    onRemoveCardsByCategory, onRegisterDndHandlers, cardWidth,
    strategy, excludedCategoryNames, onDropError, onCardMoved, onCatalogCardDrop,
  });

  const groupsWithFilteredCards = useMemo(() => {
    return groups.map(g => {
      const ordered = applyManualOrder(g.name, g.cards);
      const cards = isFilterActive ? ordered.filter(card => !shouldBeTransparent(card)) : ordered;
      return { ...g, cards };
    });
  }, [groups, applyManualOrder, isFilterActive, shouldBeTransparent]);
  
  // Masonry-Layout: Berechne Position (top/left) für jeden Stack
  const stackOuterWidth = cardWidth + STACK_PADDING * 2 + STACK_BORDER_PX * 2;
  
  const masonryLayout = useMemo(() => {
    if (groupsWithFilteredCards.length === 0 || containerWidth === 0) {
      return { positions: {}, containerHeight: 0 };
    }

    // Höhe pro Stack berechnen (basierend auf gefilterten Karten)
    const stackHeights = {};
    for (const group of groupsWithFilteredCards) {
      const numCards = group.cards.length;
      // Leere Gruppen behalten Mindesthöhe einer Karte (DnD-Target)
      const cardsHeight = numCards > 0
        ? cardHeight + Math.max(0, numCards - 1) * visibleHeight
        : cardHeight;
      stackHeights[group.name] = STACK_HEADER_HEIGHT + HEADER_TO_CARDS_GAP + cardsHeight + STACK_PADDING * 2 + STACK_BORDER_PX * 2;
    }

    // Reihen-Masonry: Stacks nebeneinander solange Spalten-Höhen nicht zu weit auseinanderdriften
    const colHeights = new Array(effectiveColumnCount).fill(0);
    const positions = {};
    let placed = 0;

    for (const group of groupsWithFilteredCards) {
      if (placed > 0 && placed % effectiveColumnCount === 0) {
        const minH = Math.min(...colHeights);
        const groupMax = colHeights.reduce(
          (max, h) => (h - minH <= ROW_SNAP_THRESHOLD_PX && h > max) ? h : max, minH
        );
        for (let c = 0; c < effectiveColumnCount; c++) {
          if (colHeights[c] - minH <= ROW_SNAP_THRESHOLD_PX) {
            colHeights[c] = groupMax;
          }
        }
      }

      let minCol = 0;
      for (let c = 1; c < effectiveColumnCount; c++) {
        if (colHeights[c] < colHeights[minCol]) minCol = c;
      }

      const left = minCol * (stackOuterWidth + CARD_GAP);
      const top = colHeights[minCol];
      positions[group.name] = { left, top };

      colHeights[minCol] += stackHeights[group.name] + CARD_GAP;
      placed++;
    }

    const containerHeight = Math.max(...colHeights, 0);
    return { positions, containerHeight };
  }, [groupsWithFilteredCards, containerWidth, effectiveColumnCount, cardHeight, visibleHeight, stackOuterWidth]);
  
  if (!deck) {
    return <div style={{ color: 'var(--text-secondary)', padding: '20px' }}>Kein Deck geladen</div>;
  }
  
  return (<>
      <div ref={containerRef} style={{ overflow: 'clip' }}>
        {/* Stacks erst rendern wenn containerWidth bekannt ist */}
        {containerWidth > 0 && (
        <SortableContext items={groupsWithFilteredCards.map(g => `cat-${g.name}`)} strategy={rectSortingStrategy}>
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: masonryLayout.containerHeight,
          }}
        >
          {groupsWithFilteredCards.map((group) => {
            const pos = masonryLayout.positions[group.name];
            const externesZiel = externalDrop && group.id != null && !group.isCommander;
            return (
              <CategoryStack
                key={group.name}
                category={group.name}
                categoryId={group.id}
                excluded={!!(group.excluded || group.type === 'excluded')}
                cards={group.cards}
                isDropTarget={dropTarget === group.name || (externesZiel && externalDrop.target === group.name)}
                externalDragging={Boolean(externesZiel && externalDrop.active)}
                externalDropProps={externesZiel ? externalDrop.propsFor(group.name, group.id) : undefined}
                cardWidth={cardWidth}
                visibleHeight={visibleHeight}
                disableHover={hoverSuppressed}
                canEditQuantity={canEditQuantity}
                onQuantityDelta={onQuantityDelta}
                isOwnProfile={isOwnProfile}
                onSetAsCommander={handleSetAsCommander}
                onShowDetails={onShowDetails}
                onSwitchPrint={onSwitchPrint}
                onRemoveCategory={handleRemoveCategory}
                canRemove={strategy.editable}
                isDroppable={strategy.editable || !!group.excluded || group.type === 'excluded'}
                isDraggingCategory={isDraggingCategory}
                stickyTop={stickyTop}
                style={pos ? { position: 'absolute', left: pos.left, top: pos.top } : undefined}
                highlightedNewCardId={highlightedNewCardId}
                deckLabels={deckLabels}
                getCardLabels={getCardLabels}
                onAssignLabel={onAssignLabel}
                onRemoveLabel={onRemoveLabel}
                onNewLabelRequest={onNewLabelRequest}
                strategy={strategy}
                renameCategory={renameCategory}
                onCategoryRenamed={onCategoryRenamed}
              />
            );
          })}
        </div>
        </SortableContext>
        )}
      </div>
      <ConfirmDialog
        open={!!pendingDeleteCategory}
        onOpenChange={(next) => { if (!next) cancelDeleteCategory(); }}
        onConfirm={confirmDeleteCategory}
        title="Kategorie löschen"
        description="Wenn du diese Kategorie löschst, werden alle enthaltenen Karten aus dem Deck entfernt."
        confirmLabel="Löschen"
        tone="danger"
      />
  </>);
}
